import { useState } from 'react'
import { getTokenCombatState } from '@/utils/tokenCombatState'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { NativeSelect } from '@arcanecircle/ui/ui/native-select'
import { GrimoireTabs } from '@arcanecircle/ui/grimoire/tabs'
import { TabPanel, Tab } from './TabPanel'
import { ChatPanel } from './ChatPanel'
import { MapConfigPanel } from './MapConfigPanel'
import { MapCreationModal } from './MapCreationModal'
import { NpcTokenModal } from './NpcTokenModal'
import { HotkeysModal } from './HotkeysModal'
import { InitiativePanel } from './InitiativePanel'
import { useVTTStore } from '@/store'
import { socketService } from '@/services/socket'
import type { MapConfig, Token } from '@/types'
import { getMainPlatformUrl } from '@/services/characterService'
import type { VTTCharacterData, TokenClassification } from '@/services/characterService'
import type { VTTExpectedPlayer } from '@/services/sessionMetaService'
import { useCharacterDrag } from '@/hooks/useCharacterDrag'
import { buildBankGroups } from '@/utils/bankGroups'

interface RightSidebarProps {
  isGM: boolean
  currentUserId: string
  displayName: string
  sessionId: string
  /** VTT JWT token — forwarded to the NPC token modal for platform API calls. */
  authToken?: string
  participants: Array<{
    userId: string
    displayName: string
    role: string
  }>
  /** Roster of expected players from session metadata. Used by the
   *  chat-tab roster to render expected-but-absent rows. */
  expectedPlayers?: VTTExpectedPlayer[]
  onMapUpdate?: (mapId: string, updates: Partial<MapConfig>) => void
  onMapCreate?: (
    mapData: Partial<MapConfig>,
    imageFile: File | null,
  ) => Promise<void>
  onMapDelete?: (mapId: string) => void | Promise<void>
  /** Used by InitiativePanel's Roll button to find a token's initiative modifier. */
  getCharacter?: (characterId: string) => VTTCharacterData | null
  /** Campaign roster fetched from the platform — drives the
   *  Characters tab; GM rows are draggable onto the canvas to spawn
   *  a linked token. */
  characters?: VTTCharacterData[]
  charactersLoading?: boolean
  charactersError?: string | null
  /** Mobile drawer state — App.tsx owns it (the TopBar hamburger
   *  toggles, the backdrop tap closes). Desktop ignores both: the
   *  sidebar is permanently docked at >=769px via CSS. */
  isMobileOpen?: boolean
  onMobileClose?: () => void
}

/**
 * v5 right sidebar — hosts the tabbed surfaces (Chat / Tokens /
 * Initiative / Map). Each tab is its own component restyled against
 * the v5 design system; this file owns only the tab wiring and the
 * small inline content (Tokens + Map).
 */
export function RightSidebar({
  isGM,
  currentUserId,
  sessionId,
  authToken = '',
  participants,
  expectedPlayers,
  onMapUpdate,
  onMapCreate,
  onMapDelete,
  getCharacter,
  characters = [],
  charactersLoading,
  charactersError,
  isMobileOpen = false,
  onMobileClose,
}: RightSidebarProps) {
  const [charactersCategory, setCharactersCategory] = useState<
    'players' | 'npcs'
  >('players')
  const [showMapCreationModal, setShowMapCreationModal] = useState(false)
  const [showNpcModal, setShowNpcModal] = useState(false)
  const [showHotkeysModal, setShowHotkeysModal] = useState(false)
  const { getActiveMap, getActiveMapTokens, maps, activeMapId, switchMap } = useVTTStore()

  const map = getActiveMap()

  const chatTab: Tab = {
    id: 'chat',
    label: 'Chat',
    content: (
      <ChatPanel
        participants={participants}
        expectedPlayers={expectedPlayers}
        currentUserId={currentUserId}
      />
    ),
  }

  const playerCharacters = characters.filter((c) => {
    const type = (c.characterType || '').toLowerCase()
    return type !== 'npc' && type !== 'monster'
  })

  const charactersTab: Tab = {
    id: 'characters',
    label: 'Characters',
    content: (
      <>
        <GrimoireTabs
          variant="pill"
          value={charactersCategory}
          onValueChange={(v) => setCharactersCategory(v as 'players' | 'npcs')}
          tabs={[
            {
              value: 'players',
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.375rem' }}>
                  Players
                  <span
                    className="font-mono-ac"
                    style={{ opacity: 0.6, fontWeight: 500, fontSize: '0.6875rem' }}
                  >
                    {playerCharacters.length}
                  </span>
                </span>
              ),
            },
            { value: 'npcs', label: 'Tokens' },
          ]}
        />

        {charactersCategory === 'players' ? (
          <section className="ac-panel is-flat">
            {charactersError ? (
              <div
                className="ac-help"
                style={{
                  textAlign: 'center',
                  padding: '0.75rem 0.5rem',
                  color: 'hsl(var(--ac-danger))',
                }}
              >
                Couldn't load characters: {charactersError}
              </div>
            ) : charactersLoading ? (
              <div
                className="ac-help"
                style={{ textAlign: 'center', padding: '0.75rem 0.5rem' }}
              >
                Loading characters…
              </div>
            ) : playerCharacters.length === 0 ? (
              <div
                className="ac-help"
                style={{ textAlign: 'center', padding: '0.75rem 0.5rem' }}
              >
                No approved player characters on this campaign yet.
                <div
                  className="font-mono-ac"
                  style={{ marginTop: 6, fontSize: '0.625rem', opacity: 0.6 }}
                >
                  (raw roster from server: {characters.length} record
                  {characters.length === 1 ? '' : 's'})
                </div>
              </div>
            ) : (
              <>
                {isGM && (
                  <div
                    className="ac-help"
                    style={{ padding: '0 0.125rem 0.375rem' }}
                  >
                    Drag a character onto the map to spawn its token.
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.375rem',
                  }}
                >
                  {playerCharacters.map((character) => (
                    <CharacterRow
                      key={character.id}
                      character={character}
                      isGM={isGM}
                    />
                  ))}
                </div>
              </>
            )}
          </section>
        ) : (
          <NpcTab
            isGM={isGM}
            getCharacter={getCharacter}
            getActiveMapTokens={getActiveMapTokens}
            onShowNpcModal={() => setShowNpcModal(true)}
            characters={characters ?? []}
            activeMapId={activeMapId}
          />
        )}
      </>
    ),
  }

  const mapTab: Tab = {
    id: 'map',
    label: 'Map',
    content: (
      <>
        {/* GM-only: map selector + new-map CTA */}
        {isGM && onMapCreate && (
          <section className="ac-panel is-flat">
            <div className="ac-panel-header">
              <div className="ac-panel-eyebrow">
                {maps.length > 0 ? `Active map · ${maps.length}` : 'Maps'}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => setShowMapCreationModal(true)}
                icon={
                  <svg
                    width={11}
                    height={11}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                }
              >
                New map
              </Button>
            </div>
            {maps.length > 0 ? (
              <NativeSelect
                value={activeMapId || ''}
                onChange={(e) => {
                  const nextId = e.target.value
                  switchMap(nextId)
                  // Persist isActive on the server and broadcast to
                  // every other client in the session — otherwise
                  // players keep seeing the previously-active map.
                  try {
                    socketService.emitMapSwitch(nextId)
                  } catch (err) {
                    console.error('[RightSidebar] map_switch emit failed', err)
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {maps.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.width}x{m.height})
                  </option>
                ))}
              </NativeSelect>
            ) : (
              <div className="ac-help">
                No maps yet. Click <strong>New map</strong> to create your first one.
              </div>
            )}
          </section>
        )}

        {/* GM: editable map config.
            #527: key by map id so the panel remounts per map — its editable
            name/fields are seeded once via useState and otherwise retain the
            prior map's values (which defaulted a new map to the old name). */}
        {isGM && map && onMapUpdate && (
          <MapConfigPanel key={map.id} map={map} onUpdate={onMapUpdate} onDelete={onMapDelete} />
        )}

        {/* Non-GM: read-only map info */}
        {(!isGM || !onMapUpdate) && map && (
          <section className="ac-panel">
            <div>
              <div className="ac-panel-eyebrow">Active map</div>
              <div className="ac-panel-title">{map.name}</div>
            </div>
            <div className="vtt-stat-grid">
              <div className="vtt-stat">
                <div className="k">Size</div>
                <div className="v" style={{ fontSize: '0.875rem' }}>
                  {map.width}×{map.height}
                </div>
              </div>
              <div className="vtt-stat">
                <div className="k">Grid</div>
                <div className="v" style={{ fontSize: '0.875rem' }}>
                  {map.gridSize}
                  <small>px</small>
                </div>
              </div>
              <div className="vtt-stat">
                <div className="k">Vision</div>
                <div className="v" style={{ fontSize: '0.6875rem' }}>
                  {map.losEnabled ? 'LoS + Fog' : 'Fog'}
                </div>
              </div>
            </div>
          </section>
        )}

        {!map && (!isGM || !onMapCreate) && (
          <section className="ac-panel is-flat">
            <div className="ac-help" style={{ textAlign: 'center', padding: '0.5rem' }}>
              No map selected.
            </div>
          </section>
        )}
      </>
    ),
  }

  const settingsTab: Tab = {
    id: 'settings',
    label: 'Settings',
    icon: (
      <svg
        width={13}
        height={13}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    content: (
      <section className="ac-panel is-flat">
        <div className="ac-panel-eyebrow" style={{ marginBottom: '0.625rem' }}>
          Navigation
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              // Tabletop landing page (the VTT's own root → LandingPage),
              // not the main AC site.
              window.location.href = window.location.origin
            }}
          >
            Back to home
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              window.location.href = `${getMainPlatformUrl()}/dashboard`
            }}
          >
            Back to Arcane Circle dashboard
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setShowHotkeysModal(true)}
          >
            Show hotkeys
          </Button>
        </div>
      </section>
    ),
  }

  // GM tab retired — vision-mode, show-barriers, and selected-barrier
  // editing now live in the canvas Toolbar so all GM affordances are
  // consolidated next to the canvas.
  const gmTab: Tab | null = null

  const initiativeTab: Tab = {
    id: 'initiative',
    label: 'Initiative',
    content: <InitiativePanel isGM={isGM} getCharacter={getCharacter} />,
  }

  // Admin tab removed — it was visible to every participant and exposed the
  // destructive "Take GM control" action to players. The take_gm_control
  // socket path still exists server-side but has no client entry point now.

  const tabs: Tab[] = [chatTab, charactersTab, initiativeTab, mapTab, settingsTab]
  if (gmTab) tabs.push(gmTab)

  return (
    <>
      <aside
        className={`vtt-sidebar${isMobileOpen ? ' is-mobile-open' : ''}`}
        aria-hidden={!isMobileOpen ? undefined : false}
      >
        {/* Mobile-only close button — desktop hides it via CSS so the
            chrome stays clean. Sits above the tabs so reaching for it
            with a thumb is easy on a phone. */}
        {onMobileClose && (
          <IconButton
            size="sm"
            variant="ghost"
            className="vtt-sidebar-mobile-close"
            aria-label="Close sidebar"
            onClick={onMobileClose}
          >
            ×
          </IconButton>
        )}
        <TabPanel tabs={tabs} defaultTab="chat" />
      </aside>

      {showMapCreationModal && onMapCreate && (
        <MapCreationModal
          sessionId={sessionId}
          onClose={() => setShowMapCreationModal(false)}
          onCreate={onMapCreate}
        />
      )}

      {showNpcModal && (
        <NpcTokenModal
          authToken={authToken}
          sessionId={sessionId}
          onClose={() => setShowNpcModal(false)}
        />
      )}

      {showHotkeysModal && (
        <HotkeysModal onClose={() => setShowHotkeysModal(false)} />
      )}
    </>
  )
}

/** Small button that opens a character's full sheet modal. Stops
 *  pointer/click propagation so it never starts a spawn drag on the
 *  row it lives in. */
function ViewSheetButton({ characterId }: { characterId: string }) {
  const openCharacterSheet = useVTTStore((s) => s.openCharacterSheet)
  return (
    <button
      type="button"
      className="ac-icon-btn"
      title="Open character sheet"
      aria-label="Open character sheet"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation()
        openCharacterSheet(characterId)
      }}
      style={{ width: 26, height: 26, flexShrink: 0 }}
    >
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    </button>
  )
}

function CharacterRow({
  character,
  isGM,
}: {
  character: VTTCharacterData
  isGM: boolean
}) {
  const { startDrag } = useCharacterDrag()
  // Prefer the token-specific image, falling back to the sheet portrait — the
  // same order the spawn drag and drag ghost use (see startDrag below), and the
  // fallback semantics documented on VTTCharacterData.tokenImageUrl.
  const portrait = character.tokenImageUrl || character.imageUrl || null
  const initial = (character.name || '?').charAt(0).toUpperCase()
  // HP only renders for d20 sheets where it's meaningful. Burning
  // Wheel / generic sheets fall through to "Player · Lv N".
  // Use the same missing-value fallback as the tooltip and canvas overlay
  // (`?? 'd20'`) so a legacy D&D sheet with no systemKind still shows HP here.
  const hp =
    (character.systemKind ?? 'd20') === 'd20' && character.stats?.hp
      ? `HP ${character.stats.hp.current}/${character.stats.hp.max}`
      : null
  const subtitle = [
    character.playerName,
    hp,
    character.level != null ? `Lv ${character.level}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div
      className="ac-card-flat"
      onPointerDown={
        isGM
          ? (e) => {
              // Left button only — middle/right click + multitouch
              // should not start a spawn drag.
              if (e.button !== 0) return
              startDrag(
                {
                  characterId: character.id,
                  name: character.name,
                  imageUrl: character.tokenImageUrl || character.imageUrl,
                  // Carry the owner so the dragged token isn't owner-less —
                  // mirrors RightSidebar.handlePlace (#130) for the drag path (#164).
                  ownerUserId: character.playerId ?? undefined,
                },
                e,
              )
            }
          : undefined
      }
      title={
        isGM ? `Drag ${character.name} onto the map to spawn its token` : undefined
      }
      style={{
        padding: '0.5rem 0.625rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.625rem',
        cursor: isGM ? 'grab' : 'default',
        userSelect: 'none',
      }}
    >
      {portrait ? (
        <img
          src={portrait}
          alt=""
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
            border: '1px solid hsl(var(--ac-dark-accent))',
          }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'hsl(var(--ac-dark-primary))',
            border: '1px solid hsl(var(--ac-dark-accent))',
            color: 'hsl(var(--ac-text-muted))',
            fontFamily: 'var(--ac-font-serif)',
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          {initial}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: 'hsl(var(--ac-text-light))',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {character.name}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      <ViewSheetButton characterId={character.id} />
    </div>
  )
}

// ── NPC tab ─────────────────────────────────────────────────────────────────

/** Colors and labels for each token classification. */
const CLASS_META: Record<
  TokenClassification,
  { label: string; color: string }
> = {
  player: { label: 'Players', color: 'hsl(var(--ac-mint-primary))' },
  friendly: { label: 'Friendly NPCs', color: 'hsl(var(--ac-info, 200 80% 60%))' },
  enemy: { label: 'Enemy NPCs', color: 'hsl(var(--ac-danger))' },
}

function NpcTab({
  isGM,
  getCharacter,
  getActiveMapTokens,
  onShowNpcModal,
  characters,
  activeMapId,
}: {
  isGM: boolean
  getCharacter?: (id: string) => VTTCharacterData | null
  getActiveMapTokens: () => Token[]
  onShowNpcModal: () => void
  characters: VTTCharacterData[]
  activeMapId: string | null
}) {
  const tokens = getActiveMapTokens()

  // Build the bank from ACTORS (campaign characters), attaching the token
  // placements each has on the active map. Sourcing the list from actors
  // rather than the map's tokens is what makes this a persistent,
  // map-independent bank: deleting a token (placement) no longer removes the
  // NPC from the list, and an actor with no placement here still shows so you
  // can spawn it. Orphan tokens (no character link) are still listed so
  // nothing on the map is hidden.
  const groups = buildBankGroups(characters, tokens)

  const handlePlace = (char: VTTCharacterData) => {
    if (!activeMapId) return
    socketService.createToken({
      mapId: activeMapId,
      name: char.name,
      x: 1,
      y: 1,
      characterId: char.id,
      imageUrl: char.tokenImageUrl ?? char.imageUrl ?? undefined,
      // Carry the character's owner onto the token so the player who owns it can
      // actually move it (canMoveToken gates players to tokens they own). Without
      // this the token lands owner-less and only the GM can move it.
      ownerUserId: char.playerId ?? undefined,
    })
  }

  const totalEntries =
    groups.player.length +
    groups.friendly.length +
    groups.enemy.length +
    groups.unknown.length

  return (
    <section className="ac-panel is-flat">
      {/* GM-only action bar */}
      {isGM && (
        <div
          style={{
            display: 'flex',
            gap: '0.375rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid hsl(var(--ac-dark-accent))',
            marginBottom: '0.5rem',
          }}
        >
          <Button
            type="button"
            size="sm"
            onClick={onShowNpcModal}
            icon={
              <svg
                width={11}
                height={11}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            }
          >
            New NPC
          </Button>
        </div>
      )}

      {totalEntries === 0 ? (
        <div
          className="ac-help"
          style={{ textAlign: 'center', padding: '0.75rem 0.5rem' }}
        >
          No NPCs or characters yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {(
            ['player', 'friendly', 'enemy', 'unknown'] as const
          ).map((cls) => {
            const groupEntries = groups[cls]
            if (groupEntries.length === 0) return null
            // NB (VTT #163): this bucket is NOT "tokens on the map". It is the
            // classification fallback — campaign actors with no/unknown
            // tokenClassification (placed or not, see "Not on map" rows) plus
            // orphan tokens that have no character link. Renaming it to "On Map"
            // was requested but would misdescribe the contents, so it stays a
            // catch-all category. See buildBankGroups in utils/bankGroups.ts.
            const meta =
              cls === 'unknown'
                ? { label: 'Uncategorized', color: 'hsl(var(--ac-text-muted))' }
                : CLASS_META[cls]

            return (
              <div key={cls}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    marginBottom: '0.25rem',
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: meta.color,
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                  <span
                    style={{
                      fontSize: '0.625rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: meta.color,
                      fontWeight: 600,
                    }}
                  >
                    {meta.label}
                  </span>
                  <span
                    className="font-mono-ac"
                    style={{
                      fontSize: '0.5625rem',
                      color: 'hsl(var(--ac-text-muted))',
                      marginLeft: 'auto',
                    }}
                  >
                    {groupEntries.length}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25rem',
                  }}
                >
                  {groupEntries.map((entry) => {
                    // Placed actor (or orphan token): one row per placement,
                    // preserving the existing per-token controls.
                    if (entry.placements.length > 0) {
                      return entry.placements.map((token) => (
                        <TokenGroupRow
                          key={token.id}
                          token={token}
                          classificationColor={meta.color}
                          getCharacter={getCharacter}
                        />
                      ))
                    }
                    // Unplaced bank actor — show with a Place affordance (GM only).
                    return entry.character ? (
                      <BankActorRow
                        key={entry.character.id}
                        character={entry.character}
                        classificationColor={meta.color}
                        isGM={isGM}
                        onPlace={handlePlace}
                      />
                    ) : null
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

/**
 * A bank actor with no placement on the active map. Renders dimmed (it isn't
 * on the table) with a Place action for the GM that spawns a linked token —
 * the re-spawn path that makes the bank usable after a token delete.
 */
function BankActorRow({
  character,
  classificationColor,
  isGM,
  onPlace,
}: {
  character: VTTCharacterData
  classificationColor: string
  isGM: boolean
  onPlace: (character: VTTCharacterData) => void
}) {
  const portrait = character.tokenImageUrl ?? character.imageUrl ?? null
  const initial = (character.name || '?').charAt(0).toUpperCase()

  return (
    <div
      className="ac-card-flat"
      style={{
        padding: '0.375rem 0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        borderLeft: `2px solid ${classificationColor}`,
        opacity: 0.6,
      }}
    >
      {portrait ? (
        <img
          src={portrait}
          alt=""
          style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'hsl(var(--ac-dark-accent))',
            fontSize: '0.625rem',
            flexShrink: 0,
          }}
        >
          {initial}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {character.name}
      </span>
      <span style={{ fontSize: '0.5625rem', color: 'hsl(var(--ac-text-muted))', flexShrink: 0 }}>
        Not on map
      </span>
      {isGM && (
        <Button type="button" size="sm" onClick={() => onPlace(character)}>
          Place
        </Button>
      )}
    </div>
  )
}

function TokenGroupRow({
  token,
  classificationColor,
  getCharacter,
}: {
  token: Token
  classificationColor: string
  getCharacter?: (id: string) => VTTCharacterData | null
}) {
  const char = token.characterId && getCharacter
    ? getCharacter(token.characterId)
    : null
  const portrait = char?.tokenImageUrl ?? char?.imageUrl ?? null
  const initial = (token.name || '?').charAt(0).toUpperCase()
  const combatState = getTokenCombatState(token, char ?? null)
  const hp = (combatState.hp.max > 0 || char?.stats?.hp)
    ? `${combatState.hp.current}/${combatState.hp.max} HP`
    : null
  const mobBadge = char?.isMob ? '· Mob' : null

  return (
    <div
      className="ac-card-flat"
      style={{
        padding: '0.375rem 0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        borderLeft: `2px solid ${classificationColor}`,
      }}
    >
      {portrait ? (
        <img
          src={portrait}
          alt=""
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
          }}
        />
      ) : (
        <span
          aria-hidden
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'hsl(var(--ac-dark-primary))',
            border: '1px solid hsl(var(--ac-dark-accent))',
            color: 'hsl(var(--ac-text-muted))',
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          {initial}
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'hsl(var(--ac-text-light))',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {token.name}
        </div>
        {(hp || mobBadge) && (
          <div
            style={{
              fontSize: '0.625rem',
              color: 'hsl(var(--ac-text-muted))',
              marginTop: 1,
            }}
          >
            {[hp, mobBadge].filter(Boolean).join(' ')}
          </div>
        )}
      </div>
      {token.characterId && <ViewSheetButton characterId={token.characterId} />}
    </div>
  )
}
